import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  Menu,
  MenuItem
} from '@mui/material';
import {
  AccountBalance,
  Add as AddIcon,
  Edit,
  Delete,
  ContentCopy,
  Print,
  Search,
  FilterList,
  Close
} from '@mui/icons-material';
import { employeeService } from '../services/employeeService';
import { formatDateForInput, formatDateDisplay } from '../utils/dateUtils';
import { formatCurrency } from '../components/CurrencyTextField';
import '../styles/print.css';
import PrintPage from '../components/print/PrintPage';
import { printWithFilename } from '../utils/printUtils';

const emptyForm = () => ({
  employee_number: '',
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  phone: '',
  role: 'employee',
  is_active: true,
  date_of_hire: '',
  job_title: '',
  department: '',
  current_pay_rate: '',
  kiosk_pin: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zip_code: ''
});

const HumanResources = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [detailEmployee, setDetailEmployee] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('new'); // 'new' | 'edit' | 'copy'
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeFilter, setActiveFilter] = useState('active'); // 'active' | 'all'
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [hireDateFrom, setHireDateFrom] = useState('');
  const [hireDateTo, setHireDateTo] = useState('');
  const [printMenuAnchor, setPrintMenuAnchor] = useState(null);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        search: searchTerm || undefined,
        activeOnly: activeFilter === 'active' ? 'true' : '',
        department: departmentFilter?.trim() || undefined,
        hireDateFrom: hireDateFrom || undefined,
        hireDateTo: hireDateTo || undefined,
        limit: 500
      };
      const response = await employeeService.getAll(params);
      setEmployees(response.data ?? []);
    } catch (err) {
      console.error('Error loading employees:', err);
      setError(err.response?.data?.message || 'Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees();
  }, [activeFilter]);

  const handleSearch = () => {
    loadEmployees();
  };

  const handleActiveFilter = (value) => {
    setActiveFilter(value);
  };

  const openNew = () => {
    setFormData(emptyForm());
    setDialogMode('new');
    setDialogOpen(true);
  };

  const openEdit = () => {
    const e = detailEmployee || selectedEmployee;
    if (!e) return;
    setFormData({
      employee_number: e.employee_number != null ? String(e.employee_number) : '',
      first_name: e.first_name ?? '',
      last_name: e.last_name ?? '',
      email: e.email ?? '',
      password: '',
      phone: e.phone ?? '',
      role: e.role ?? 'employee',
      is_active: e.is_active !== false,
      date_of_hire: formatDateForInput(e.date_of_hire),
      job_title: e.job_title ?? '',
      department: e.department ?? '',
      current_pay_rate: e.current_pay_rate != null ? String(e.current_pay_rate) : '',
      kiosk_pin: e.kiosk_pin ?? '',
      address_line1: e.address_line1 ?? '',
      address_line2: e.address_line2 ?? '',
      city: e.city ?? '',
      state: e.state ?? '',
      zip_code: e.zip_code ?? ''
    });
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const openCopy = () => {
    const e = detailEmployee || selectedEmployee;
    if (!e) return;
    setFormData({
      employee_number: '',
      first_name: e.first_name ?? '',
      last_name: e.last_name ?? '',
      email: '',
      password: '',
      kiosk_pin: '',
      phone: e.phone ?? '',
      role: e.role ?? 'employee',
      is_active: true,
      date_of_hire: formatDateForInput(e.date_of_hire),
      job_title: e.job_title ?? '',
      department: e.department ?? '',
      current_pay_rate: e.current_pay_rate != null ? String(e.current_pay_rate) : '',
      address_line1: e.address_line1 ?? '',
      address_line2: e.address_line2 ?? '',
      city: e.city ?? '',
      state: e.state ?? '',
      zip_code: e.zip_code ?? ''
    });
    setDialogMode('copy');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const { first_name, last_name, email } = formData;
    if (!first_name?.trim() || !last_name?.trim() || !email?.trim()) {
      setError('First name, last name, and email are required.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      if (dialogMode === 'edit' && selectedEmployee?.id) {
        const payload = { ...formData };
        delete payload.password;
        if (payload.employee_number === '' || payload.employee_number == null) {
          delete payload.employee_number;
        } else {
          payload.employee_number = parseInt(payload.employee_number, 10);
        }
        await employeeService.update(selectedEmployee.id, payload);
        const res = await employeeService.getById(selectedEmployee.id);
        setDetailEmployee(res.data ?? null);
        await loadEmployees();
      } else {
        const payload = { ...formData };
        if (payload.employee_number === '' || payload.employee_number == null) {
          delete payload.employee_number;
        } else {
          payload.employee_number = parseInt(payload.employee_number, 10);
        }
        await employeeService.create(payload);
        await loadEmployees();
      }
      setDialogOpen(false);
    } catch (err) {
      console.error('Error saving employee:', err);
      setError(err.response?.data?.message || 'Failed to save employee');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => setDeleteDialogOpen(true);

  const handleDeleteConfirm = async () => {
    if (!selectedEmployee?.id) return;
    try {
      setDeleting(true);
      setError('');
      await employeeService.delete(selectedEmployee.id);
      setDeleteDialogOpen(false);
      handleClearSelection();
      await loadEmployees();
    } catch (err) {
      console.error('Error deleting employee:', err);
      setError(err.response?.data?.message || 'Failed to delete employee');
    } finally {
      setDeleting(false);
    }
  };

  const handlePrint = () => {
    const employeeNumber = selectedEmployee?.employee_number;
    printWithFilename(employeeNumber ? `Employee-${employeeNumber}` : 'Employees');
  };

  const handleEmployeeRowClick = async (emp) => {
    setSelectedEmployee(emp);
    setDetailEmployee(null);
    setDetailLoading(true);
    try {
      const res = await employeeService.getById(emp.id);
      setDetailEmployee(res.data ?? null);
    } catch (err) {
      console.error('Error loading employee detail:', err);
      setError(err.response?.data?.message || 'Failed to load employee');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedEmployee(null);
    setDetailEmployee(null);
  };

  const fullName = (e) =>
    [e?.first_name, e?.last_name].filter(Boolean).join(' ') || '—';

  const formatDate = formatDateDisplay;

  return (
    <Box>
      <Box className="no-print-hr">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Human Resources
        </Typography>
        <Box>
          <IconButton size="small" color="primary" title="Edit" disabled={!selectedEmployee} onClick={openEdit}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" title="Delete" disabled={!selectedEmployee} onClick={handleDeleteClick}>
            <Delete fontSize="small" />
          </IconButton>
          <IconButton size="small" color="primary" title="Copy" disabled={!selectedEmployee} onClick={openCopy}>
            <ContentCopy fontSize="small" />
          </IconButton>
          <IconButton size="small" color="primary" title="Print" onClick={(e) => setPrintMenuAnchor(e.currentTarget)}>
            <Print fontSize="small" />
          </IconButton>
          <Menu anchorEl={printMenuAnchor} open={!!printMenuAnchor} onClose={() => setPrintMenuAnchor(null)} disableEnforceFocus disableRestoreFocus>
            <MenuItem onClick={() => { printWithFilename('Employees'); setPrintMenuAnchor(null); }}>Print list</MenuItem>
            <MenuItem
              onClick={() => { handlePrint(); setPrintMenuAnchor(null); }}
              disabled={!selectedEmployee}
            >
              Print selected
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }}>
          {error}
        </Alert>
      )}

      {/* Selected Employee Information Bar */}
      <Paper sx={{ p: 1.5, mb: 1.5, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Employee Name</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedEmployee ? fullName(detailEmployee || selectedEmployee) : '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Employee ID</Typography>
            <Typography variant="body2">{(detailEmployee || selectedEmployee)?.employee_number ?? '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Status</Typography>
            {(detailEmployee || selectedEmployee) ? (
              <Chip
                label={(detailEmployee || selectedEmployee).is_active ? 'Active' : 'Inactive'}
                color={(detailEmployee || selectedEmployee).is_active ? 'success' : 'default'}
                size="small"
              />
            ) : (
              <Typography variant="body2">—</Typography>
            )}
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Hire Date</Typography>
            <Typography variant="body2">{selectedEmployee ? (formatDate((detailEmployee || {}).date_of_hire) || '—') : '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Job Title</Typography>
            <Typography variant="body2">{selectedEmployee ? ((detailEmployee || {}).job_title || (selectedEmployee?.role ? String(selectedEmployee.role).replace(/_/g, ' ') : '') || '—') : '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Dept</Typography>
            <Typography variant="body2">{(detailEmployee || selectedEmployee)?.department ?? '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Pay Rate</Typography>
            <Typography variant="body2">{(detailEmployee || selectedEmployee) ? formatCurrency((detailEmployee || {}).current_pay_rate) : '—'}</Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton size="small" onClick={handleClearSelection} disabled={!selectedEmployee} sx={{ ml: 'auto' }} title="Clear selection">
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Paper>

      {/* Search and filters */}
      <Paper sx={{ p: 1, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button variant="contained" color="success" size="small" startIcon={<AddIcon />} onClick={openNew}>
            New Employee
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <FilterList fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Status:</Typography>
            <Chip
              label="Active"
              size="small"
              onClick={() => handleActiveFilter('active')}
              color={activeFilter === 'active' ? 'primary' : 'default'}
              sx={{ cursor: 'pointer' }}
            />
            <Chip
              label="All"
              size="small"
              onClick={() => handleActiveFilter('all')}
              color={activeFilter === 'all' ? 'primary' : 'default'}
              sx={{ cursor: 'pointer' }}
            />
          </Box>
          <TextField
            size="small"
            placeholder="Department"
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ width: 120 }}
            InputProps={{ sx: { '& input': { py: 0.5 } } }}
          />
          <TextField
            size="small"
            label="Hire from"
            type="date"
            value={hireDateFrom}
            onChange={(e) => setHireDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 130 }}
            inputProps={{ 'aria-label': 'Hire date from' }}
          />
          <TextField
            size="small"
            label="Hire to"
            type="date"
            value={hireDateTo}
            onChange={(e) => setHireDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 130 }}
            inputProps={{ 'aria-label': 'Hire date to' }}
          />
          <Typography variant="body2" sx={{ flexGrow: 1 }}>
            Showing Employee Record(s){activeFilter === 'all' ? ' (active + inactive)' : ''}
          </Typography>
          <TextField
            size="small"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearch} edge="end" size="small">
                    <Search />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 260 }}
          />
        </Box>
      </Paper>

      <Grid container spacing={2}>
        {/* Employee Navigator */}
        <Grid item xs={12} md={5}>
          <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Employee Name</Typography></TableCell>
                  <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Employee ID</Typography></TableCell>
                  <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Status</Typography></TableCell>
                  <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Hire Date</Typography></TableCell>
                  <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Job Title</Typography></TableCell>
                  <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Dept</Typography></TableCell>
                  <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Pay Rate</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : employees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="textSecondary">No employees found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  employees.map((emp) => (
                    <TableRow
                      key={emp.id}
                      hover
                      onClick={() => handleEmployeeRowClick(emp)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: selectedEmployee?.id === emp.id ? 'action.selected' : 'inherit'
                      }}
                    >
                      <TableCell>{fullName(emp)}</TableCell>
                      <TableCell>{emp.employee_number ?? '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={emp.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={emp.is_active ? 'success' : 'default'}
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>{formatDate(emp.date_of_hire)}</TableCell>
                      <TableCell>{emp.job_title || (emp.role ? String(emp.role).replace(/_/g, ' ') : '') || '—'}</TableCell>
                      <TableCell>{emp.department || '—'}</TableCell>
                      <TableCell>{formatCurrency(emp.current_pay_rate)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* Employee Inquiry + Payroll */}
        <Grid item xs={12} md={7}>
          {!selectedEmployee ? (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Employee Inquiry</Typography>
              <Typography variant="body2" color="textSecondary">
                No employee selected. Select an employee from the list to view details and payroll.
              </Typography>
            </Paper>
          ) : detailLoading ? (
            <Paper sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
              <CircularProgress size={24} />
            </Paper>
          ) : !detailEmployee ? (
            <Paper sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Could not load employee details. Select again or try later.
              </Typography>
            </Paper>
          ) : (
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <AccountBalance fontSize="small" color="action" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Payroll section — {fullName(detailEmployee)}
                  </Typography>
                  <IconButton size="small" color="primary" title="Edit employee" onClick={openEdit} sx={{ ml: 0.5 }}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  Date of hire, work history (rehire dates), current rate of pay, pay history with date stamps.
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">Date of hire</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatDate(detailEmployee?.date_of_hire)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">Current rate of pay</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatCurrency(detailEmployee?.current_pay_rate)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">Address</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {(() => {
                        const d = detailEmployee;
                        const line1 = [d?.address_line1, d?.address_line2].filter(Boolean).join(', ');
                        const line2 = [d?.city, [d?.state, d?.zip_code].filter(Boolean).join(' ')].filter(Boolean).join(', ');
                        return line1 || line2 ? [line1, line2].filter(Boolean).join(' — ') : '—';
                      })()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Work history (rehire dates)</Typography>
                    {!detailEmployee?.workHistory?.length ? (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    ) : (
                      <List dense disablePadding>
                        {(detailEmployee.workHistory || []).map((entry, i) => (
                          <ListItem key={entry.id || i} disablePadding>
                            <ListItemText primary={formatDate(entry.effective_date)} secondary={entry.notes} />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Pay history (with date stamps)</Typography>
                    {!detailEmployee?.payHistory?.length ? (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    ) : (
                      <List dense disablePadding>
                        {(detailEmployee.payHistory || []).map((entry, i) => (
                          <ListItem key={entry.id || i} disablePadding>
                            <ListItemText primary={formatDate(entry.effective_date)} secondary={`${formatCurrency(entry.rate)} (${entry.rate_type || 'REGULAR'})`} />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Grid>
                </Grid>
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary">
                    Pay rates: REGULAR, 2ND SHIFT, 3RD SHIFT, VACATION, Rate 5–10. Last Review, Next Review, Date Of Termination — edit via Employee record when needed.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Employee form dialog (New / Edit / Copy) */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'new' ? 'New Employee' : dialogMode === 'edit' ? 'Edit Employee' : 'Copy Employee'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label={dialogMode === 'edit' ? 'Employee ID # (change for legacy — must be unique)' : 'Employee ID # (optional — leave blank to auto-assign)'}
                type="number"
                inputProps={{ min: 1, step: 1 }}
                value={formData.employee_number}
                onChange={(e) => setFormData({ ...formData, employee_number: e.target.value })}
                placeholder="Auto"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="First name" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} required />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Last name" value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
            </Grid>
            {(dialogMode === 'new' || dialogMode === 'copy') && (
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Password (leave blank for default)" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="ChangeMe123!" inputProps={{ autoComplete: 'new-password' }} />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Address</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Address line 1" value={formData.address_line1} onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Address line 2" value={formData.address_line2} onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="City" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth size="small" label="State" value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} placeholder="e.g. CA" inputProps={{ maxLength: 2 }} />
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth size="small" label="ZIP" value={formData.zip_code} onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select value={formData.role} label="Role" onChange={(e) => setFormData({ ...formData, role: e.target.value })}>
                  <MenuItem value="employee">Employee</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {dialogMode === 'edit' && (
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={formData.is_active} label="Status" onChange={(e) => setFormData({ ...formData, is_active: e.target.value })}>
                    <MenuItem value={true}>Active</MenuItem>
                    <MenuItem value={false}>Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Date of hire"
                type="date"
                value={formData.date_of_hire}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormData({ ...formData, date_of_hire: v });
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: '2099-12-31' }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Job title" value={formData.job_title} onChange={(e) => setFormData({ ...formData, job_title: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Department" value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Current pay rate" type="number" inputProps={{ step: 0.01, min: 0 }} value={formData.current_pay_rate} onChange={(e) => setFormData({ ...formData, current_pay_rate: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Kiosk PIN (3 digits, for Data Collection)" value={formData.kiosk_pin} onChange={(e) => setFormData({ ...formData, kiosk_pin: e.target.value.replace(/\D/g, '').slice(0, 10) })} placeholder="e.g. 123" inputProps={{ maxLength: 10 }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete employee?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will set the employee to inactive. They will no longer appear in the active list. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleting}>{deleting ? 'Deleting…' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>
      </Box>

      {/* Print view – selected employee summary */}
      {(detailEmployee || selectedEmployee) && (
        <PrintPage title="Employee Inquiry" showSpacer={false}>
          <div className="print-quote-details-box" style={{ marginBottom: 16 }}>
            <div className="print-quote-details-left">
              <div>
                <strong>Employee:</strong> {fullName(detailEmployee || selectedEmployee)}
              </div>
              <div>
                <strong>ID:</strong> {(detailEmployee || selectedEmployee).employee_number ?? '—'}
              </div>
            </div>
            <div className="print-quote-details-right">
              <div>
                <strong>Status:</strong>{' '}
                {(detailEmployee || selectedEmployee).is_active ? 'Active' : 'Inactive'}
              </div>
            </div>
          </div>
          <div className="print-section">
            <strong>Hire date:</strong> {formatDate((detailEmployee || {}).date_of_hire)} &emsp;{' '}
            <strong>Job title:</strong> {(detailEmployee || {}).job_title || '—'} &emsp;{' '}
            <strong>Dept:</strong> {(detailEmployee || {}).department || '—'} &emsp;{' '}
            <strong>Pay rate:</strong> {formatCurrency((detailEmployee || {}).current_pay_rate)}
          </div>
          <div className="print-section">
            <strong>Email:</strong> {(detailEmployee || selectedEmployee).email ?? '—'} &emsp;{' '}
            <strong>Phone:</strong> {(detailEmployee || selectedEmployee).phone ?? '—'}
          </div>
          {((detailEmployee || selectedEmployee).address_line1 ||
            (detailEmployee || selectedEmployee).city) && (
            <div className="print-section">
              <strong>Address:</strong>{' '}
              {[(detailEmployee || selectedEmployee).address_line1,
              (detailEmployee || selectedEmployee).address_line2]
                .filter(Boolean)
                .join(', ')}
              {((detailEmployee || selectedEmployee).city ||
                (detailEmployee || selectedEmployee).state ||
                (detailEmployee || selectedEmployee).zip_code) &&
                ` — ${[
                  (detailEmployee || selectedEmployee).city,
                  [
                    (detailEmployee || selectedEmployee).state,
                    (detailEmployee || selectedEmployee).zip_code
                  ]
                    .filter(Boolean)
                    .join(' ')
                ]
                  .filter(Boolean)
                  .join(', ')}`}
            </div>
          )}
        </PrintPage>
      )}
    </Box>
  );
};

export default HumanResources;
